// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { CameraCalibration, StrategyType, UseCase } from '@tls/sw91-communication/types/com.base';
import { ProjectSettings } from 'components/projectSelection/common/ProjectSettingTypes';
import { DEFAULT_CAMERA_CALIBRATON } from 'hooks/useCameraCalibration';
import { isProjectNameValid } from 'utilities/validations';

export type RequestSettings = {
  settings: ProjectSettings;
  cameraCalibration: CameraCalibration;
};

type ChangeRequirementsProps = {
  id?: string;
  sourceUseCase?: UseCase;
  sourceStrategy?: StrategyType;
  sourceForegroundLabelCount?: number;
  activeUseCase: UseCase;
  settings: ProjectSettings;
  loadingHasModel: boolean;
  modelCheckFailed: boolean;
  hasModel: boolean;
  hasMultiLabelCDPermision: boolean;
};

type CanSaveProps = Pick<
  ReturnType<typeof getProjectChangeRequirements>,
  | 'sourceSettingsLoaded'
  | 'currentForegroundLabelCount'
  | 'modelCheckPending'
  | 'requiresResetConfirmation'
  | 'requiresImageLabelResetConfirmation'
  | 'hasMultiLabelLicenseError'
> & {
  settings: ProjectSettings;
  loading: boolean;
  resetConfirmed: boolean;
  imageLabelResetConfirmed: boolean;
};

export function getSettingsForRequest(
  settings: ProjectSettings,
  cameraCalibration: CameraCalibration
): RequestSettings {
  if (settings.useCase === UseCase.COMPONENT_DETECTION) {
    return {
      settings: { ...settings },
      cameraCalibration: { ...DEFAULT_CAMERA_CALIBRATON },
    };
  }

  return {
    settings: { ...settings, strategy: StrategyType.SIMPLE_STRATEGY },
    cameraCalibration: { ...cameraCalibration },
  };
}

export function getProjectChangeRequirements({
  id,
  sourceUseCase,
  sourceStrategy,
  sourceForegroundLabelCount,
  activeUseCase,
  settings,
  loadingHasModel,
  modelCheckFailed,
  hasModel,
  hasMultiLabelCDPermision,
}: ChangeRequirementsProps) {
  const sourceSettingsLoaded =
    !id ||
    (sourceUseCase !== undefined &&
      sourceStrategy !== undefined &&
      sourceForegroundLabelCount !== undefined &&
      settings.projectName.trim().length > 0);
  const currentForegroundLabelCount = Math.max(settings.labelsInfo.length - 1, 0);
  const labelCountChanged =
    Boolean(id) &&
    sourceForegroundLabelCount !== undefined &&
    currentForegroundLabelCount !== sourceForegroundLabelCount;
  const labelCountReduced =
    Boolean(id) && sourceForegroundLabelCount !== undefined && currentForegroundLabelCount < sourceForegroundLabelCount;
  const useCaseChanged = Boolean(id) && sourceUseCase !== undefined && sourceUseCase !== activeUseCase;
  const modelTypeChanged =
    Boolean(id) &&
    sourceUseCase === UseCase.COMPONENT_DETECTION &&
    activeUseCase === UseCase.COMPONENT_DETECTION &&
    sourceStrategy !== undefined &&
    sourceStrategy !== settings.strategy;
  const requiresTrainingDataReset = sourceSettingsLoaded && (useCaseChanged || modelTypeChanged || labelCountChanged);

  return {
    sourceSettingsLoaded,
    currentForegroundLabelCount,
    requiresTrainingDataReset,
    modelCheckPending: requiresTrainingDataReset && loadingHasModel,
    requiresResetConfirmation: requiresTrainingDataReset && (hasModel || modelCheckFailed),
    requiresImageLabelResetConfirmation: sourceSettingsLoaded && labelCountReduced,
    hasMultiLabelLicenseError:
      sourceSettingsLoaded &&
      Boolean(id) &&
      activeUseCase === UseCase.COMPONENT_DETECTION &&
      !hasMultiLabelCDPermision &&
      currentForegroundLabelCount > 1,
  };
}

export function canSaveProject({
  settings,
  currentForegroundLabelCount,
  loading,
  sourceSettingsLoaded,
  modelCheckPending,
  hasMultiLabelLicenseError,
  requiresResetConfirmation,
  resetConfirmed,
  requiresImageLabelResetConfirmation,
  imageLabelResetConfirmed,
}: CanSaveProps): boolean {
  const formIsValid =
    settings.projectName.trim().length > 0 &&
    currentForegroundLabelCount >= 1 &&
    isProjectNameValid(settings.projectName);

  return (
    formIsValid &&
    !loading &&
    sourceSettingsLoaded &&
    !modelCheckPending &&
    !hasMultiLabelLicenseError &&
    (!requiresResetConfirmation || resetConfirmed) &&
    (!requiresImageLabelResetConfirmation || imageLabelResetConfirmed)
  );
}
